import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import { articleModel } from "@/models/article/article";
import router from "@/router";
import store from "@/store";
import useDate from "@/utils/hooks/useDate";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";
import usePagination from "@/views/components/pagination/usePagination";
import { onBeforeMount, reactive, ref, watch } from "@vue/composition-api";

import usePermissionByAccount from "@/constants/navigation/BOYNYKJMS2EH/permission-by-account"

export default function useArticle() {
  const { getPermissionModule } = usePermissionByAccount()
  const {
    fetchArticleListModel,
    articleSectionSortModel,
    deleteArticleModel,
    editArticleModel,
  } = articleModel();
  const { pagination } = usePagination();
  const { resolveDateWithSeperator, resolveTime } = useDate();
  const { openModal, closeModal } = useModal();
  const {
    openSuccessModal,
    openErrorModal,
    openDefaultErrorModal,
    openWarningModal,
  } = useValidationModal();
  const keyChange = ref(0);
  const currentIndex = ref(null);
  const currentIdSelect = ref(null);
  const tableModeReorder = ref(false);

  const {
    filters,
    searchValue,
    ordering,
    onSearch,
    sortingChanged,
    onSelectFilter,
  } = useFilterHeaderTable();

  const fields = [
    { key: "seq", label: "No." },
    { key: "article_cover", label: "Image" },
    { key: "article_title", label: "Article Name", sortable: true },
    { key: "category_id.category_name_th", label: "Category" },
    { key: "valid_from", label: "Valid From" },
    { key: "valid_through", label: "Valid Through" },
    { key: "criteria_type_desc", label: "Reward" },
    { key: "alt_reference", label: "Reference" },
    { key: "status", label: "Status", sortable: true },
    { key: "actions", label: "" },
  ];

  const modalPopup = reactive({
    report: false,
  });

  const onOpenExportModal = () => {
    modalPopup.report = true;
    openModal();
  };

  const closeReportModal = () => {
    modalPopup.report = false;
    closeModal();
  };
  const modalType = reactive({
    reorder: false,
  });

  const loading = reactive({
    tableData: false,
    api: false,
  });

  const articleActionList = reactive({
    edit: { label: "Edit", value: 1, icon: "EditIcon", display: true },
    reorder: { label: "Reorder", value: 2, icon: "ReorderIcon", display: true },
    delete: { label: "Delete", value: 3, icon: "DeleteIcon", display: true },
  });

  const articleTable = reactive({
    payload: [],
  });

  const statusOption = ref([
    { label: "Active", value: 1 },
    { label: "Inactive", value: 0 },
  ]);

  const fetchArticleList = (loadingAction = true) => {
    if (loadingAction) {
      loading.tableData = true;
    }
    fetchArticleListModel.payload.Page = pagination.currentPage;
    fetchArticleListModel.payload.Limit = pagination.perPage;
    fetchArticleListModel.payload.Filters = filters;
    fetchArticleListModel.payload.Ordering = ordering.value;

    store
      .dispatch("api/apiRequest", fetchArticleListModel)
      .then((response) => {
        articleTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const onToggleStatus = (status, article) => {
    editArticleModel.payload.Article_Id = article.article_id;
    editArticleModel.payload.Seq = article.seq;
    editArticleModel.payload.Article_Title = article.article_title;
    editArticleModel.payload.Category_Id = article.category_id?.category_id;
    editArticleModel.payload.Status = status ? 1 : 0;
    editArticleModel.payload.Article_Content_TH = article.article_content_th;
    editArticleModel.payload.Article_Content_EN = article.article_content_en;
    editArticleModel.payload.Valid_From = resolveDateWithSeperator(
      article.valid_from
    );
    editArticleModel.payload.Valid_Through = resolveDateWithSeperator(
      article.valid_through
    );
    editArticleModel.payload.Valid_From_Time = resolveTime(article.valid_from);
    editArticleModel.payload.Valid_Through_Time = resolveTime(
      article.valid_through
    );

    store
      .dispatch("api/apiFormRequest", editArticleModel)
      .catch((err) => openDefaultErrorModal(err))
      .finally(() => {
        editArticleModel.payload = {
          Seq: null,
          Article_Id: null,
          Article_Title: null,
          Article_Content_TH: null,
          Article_Content_EN: null,
          Status: null,
          Article_Cover_Url: null,
          Square_Image_Url: null,
          Valid_From: null,
          Valid_From_Time: null,
          Valid_Through: null,
          Valid_Through_Time: null,
          Remark: null,
          Description: null,
          Category_Id: null,
          Privilege_Type: null,
        };

        fetchArticleList(false);
      });
  };

  const deleteArticle = (articleId) => {
    deleteArticleModel.payload.Article_Id = articleId;

    store
      .dispatch("api/apiRequest", deleteArticleModel)
      .then((response) => {
        openSuccessModal(
          "ทำรายการสำเร็จ !",
          null,
          "ตกลง",
          () => fetchArticleList(false),
          () => fetchArticleList(false)
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      });
  };

  const onFilterEntity = (numOfEntity) => {
    pagination.perPage = numOfEntity;
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchArticle = (input) => {
    searchValue.query = input;
    onSearch("article_title");
  };

  const resolveArticleAction = (type, item) => {
    switch (type) {
      case 1:
        router.push(`/article/article/${item.item.article_id}/info`);
        return;
      case 2:
        modalType.reorder = true;
        openReorderModal(item.item);
        return;
      case 3:
        return openWarningModal(
          "ลบ Article",
          `ต้องการลบ Article นี้ใช่หรือไม่`,
          () => {
            deleteArticle(item.item.article_id);
          }
        );
      default:
        return;
    }
  };

  const openReorderModal = (item) => {
    currentIndex.value = item.seq;
    currentIdSelect.value = item.article_id;
    openModal();
  };

  const reorderArticle = async (current, newIndex) => {
    fetchArticleReorder(currentIdSelect.value, currentIndex.value, newIndex);
    keyChange.value++;
    closeModal();
  };

  const handleRowChange = (event) => {
    fetchArticleReorder(
      articleTable.payload[event.oldDraggableIndex].article_id,
      articleTable.payload[event.oldDraggableIndex].seq,
      articleTable.payload[event.newDraggableIndex].seq
    );
    articleTable.payload.splice(
      event.newDraggableIndex,
      0,
      articleTable.payload.splice(event.oldDraggableIndex, 1)[0]
    );
    keyChange.value++;
  };

  const fetchArticleReorder = (articleId, currentPostion, newPosition) => {
    loading.tableData = true;
    articleSectionSortModel.payload.Article_Id = articleId;
    articleSectionSortModel.payload.FromSeq = currentPostion;
    articleSectionSortModel.payload.ToSeq = parseInt(newPosition);

    store
      .dispatch("api/apiRequest", articleSectionSortModel)
      .then((response) => {
        fetchArticleList(false);
      })
      .catch((err) => {
        openDefaultErrorModal(err);
        loading.tableData = false;
      });
  };

  const onClickReorder = () => {
    tableModeReorder.value = !tableModeReorder.value;
    sortableRowOptions.disabled = !tableModeReorder.value;
  };

  const sortableRowOptions = reactive({
    chosenClass: "is-selected",
    ghostClass: "sortable-ghost",
    onEnd: handleRowChange,
    disabled: true,
    animation: 300,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  });

  onBeforeMount(() => {
    fetchArticleList();
  });

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchArticleList();
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;

      fetchArticleList();
    },
    { deep: true }
  );

  return {
    statusOption,
    fields,
    loading,
    articleTable,
    articleActionList,
    keyChange,
    sortableRowOptions,
    currentIndex,
    modalType,
    tableModeReorder,
    pagination,
    ordering,
    onSelectFilter,
    onFilterEntity,
    onPageChange,
    onSearchArticle,
    sortingChanged,
    closeModal,
    resolveArticleAction,
    onClickReorder,
    resolveDateWithSeperator,
    reorderArticle,
    onToggleStatus,
    onOpenExportModal,
    closeReportModal,
    modalPopup,
    getPermissionModule
  };
}
